/**
 * Chat Page - Text Chat Environment
 * Clean, modern chat interface matching redesign.jsx specifications
 * Validates: Requirements 13.1, 13.2, 13.6, 18.1, 20.4, 22.1, 22.2
 */
import { useEffect, useState } from 'react';
import { useChatStore } from '../stores/chatStore';
import {
  ModelSelector,
  MessageList,
  ChatInput,
  ExportShareModal,
} from '../components/chat';
import { Icons } from '../components/icons';
import { THEME } from '../styles/theme';

export function ChatPage() {
  const [showExportShare, setShowExportShare] = useState(false);

  // Chat store state
  const {
    currentSession,
    currentSessionId,
    messages,
    isStreaming,
    streamingContent,
    selectedModel,
    selectedProvider,
    promptEnhancerEnabled,
    error,
    // Actions
    setSelectedModel,
    togglePromptEnhancer,
    loadSessions,
    sendMessage,
    clearError,
  } = useChatStore();

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Handle send message
  const handleSendMessage = async (content: string) => {
    await sendMessage(content);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Chat Header - Simplified with model selector only */}
      <div
        className="flex items-center justify-between px-6 border-b"
        style={{
          height: '70px',
          backgroundColor: THEME.glassSurface,
          backdropFilter: 'blur(20px)',
          borderColor: THEME.border,
        }}
      >
        <div className="flex items-center gap-4">
          {/* Model Selector */}
          <ModelSelector
            selectedModel={selectedModel}
            selectedProvider={selectedProvider}
            onSelect={setSelectedModel}
            disabled={isStreaming}
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Export/Share button - only show when there's a session */}
          {currentSessionId && messages.length > 0 && (
            <button
              onClick={() => setShowExportShare(true)}
              className="p-2.5 rounded-xl transition-all duration-200 border"
              style={{
                backgroundColor: 'rgba(255,255,255,0.03)',
                borderColor: THEME.border,
                color: THEME.textMuted,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = THEME.textMain;
                e.currentTarget.style.borderColor = THEME.borderHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = THEME.textMuted;
                e.currentTarget.style.borderColor = THEME.border;
              }}
              title="Export or share conversation"
            >
              <Icons.Share size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div
          className="px-6 py-3 border-b flex items-center justify-between"
          style={{
            backgroundColor: THEME.errorSoft,
            borderColor: 'rgba(239, 68, 68, 0.3)',
          }}
        >
          <p className="text-sm" style={{ color: THEME.error }}>
            {error}
          </p>
          <button
            onClick={clearError}
            className="p-1 rounded transition-colors"
            style={{ color: THEME.error }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Icons.X size={16} />
          </button>
        </div>
      )}

      {/* Messages */}
      <MessageList
        messages={messages}
        isStreaming={isStreaming}
        streamingContent={streamingContent}
      />

      {/* Input */}
      <ChatInput
        onSend={handleSendMessage}
        disabled={isStreaming}
        promptEnhancerEnabled={promptEnhancerEnabled}
        onToggleEnhancer={togglePromptEnhancer}
      />

      {/* Export/Share Modal */}
      {currentSessionId && (
        <ExportShareModal
          isOpen={showExportShare}
          onClose={() => setShowExportShare(false)}
          sessionId={currentSessionId}
          sessionTitle={currentSession?.title || null}
        />
      )}
    </div>
  );
}
